import { isValidElement, useMemo, type ReactNode } from "react";

/**
 * Hook to determine if a component with asChild support should be interactive.
 * Returns true only when asChild is true and the child is an interactive element
 * (a, button, or Link component). All components are non-interactive by default.
 */
export default function useIsInteractive({
  asChild,
  children,
}: {
  asChild: boolean;
  children: ReactNode;
}) {
  return useMemo(() => {
    // Non-interactive by default
    if (!asChild || !children) return false;

    // When using asChild, check if the child is an interactive element
    if (isValidElement(children)) {
      const childType = children.type;

      // Check for string types (native HTML elements)
      if (typeof childType === "string") {
        return childType === "a" || childType === "button";
      }

      // Check for component types (including Link components)
      if (typeof childType === "function" || typeof childType === "object") {
        const componentName =
          (childType as { displayName?: string }).displayName ||
          (childType as { name?: string }).name ||
          "";

        // Match common Link component names
        return componentName === "Link" || componentName.endsWith("Link");
      }
    }

    return false;
  }, [asChild, children]);
}
